import React, { useState, useMemo, useEffect } from 'react';
import Modal from '@material-ui/core/Modal';
import { useRouter } from 'next/router';
import {
  OrganismProps,
  DefaultData,
  DefaultArrayData,
  DefaultObjectData,
  DefaultPayload,
  hotelType,
  Region,
  CustomType,
} from './RFPComponent.types';
import { Styled } from './RFPComponent.styles';
import { serverData } from './context';
import { Properties } from '../../molecules/Properties';
import { EventInformation } from '../../molecules/EventInformation';
import { ContactInformation } from '../../molecules/ContactInformation';
import { EventDetails } from '../../molecules/EventDetails';
import { CalendarComponent } from '../../molecules/EventDates';
import { BREAKPOINT_TABLET } from '../../constants/lib/application-breakpoints';
import { emailRegex, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { findEmailByMarshaCode, getRfpSubmissionDateFormatPattern, updateDatalayerObjForRFP } from '../../utils';
import { PrivateDining } from '../../molecules/PrivateDining';
import clsx from 'clsx';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _satellite: Record<string, any>;
  }
}

export const RFPComponent: React.FC<OrganismProps> = props => {
  const value = useMemo(() => {
    return { jsonContent: props?.model.rfpjson?.key };
  }, [props?.model.rfpjson?.key]);
  const router = useRouter();
  let marsha = router?.query?.['marshaCode']?.toString() ?? '';
  let marshaMatch = false;
  const hotelList = props?.model.rfpjson?.key?.instance?.propertiesOfInterest?.hotelList || [];
  const regionList = props?.model.rfpjson?.key?.instance?.propertiesOfInterest?.regions || [];
  const isTabletView = useCheckBreakpoint('viewportM');

  if (marsha && hotelList.length > 0) {
    hotelList?.forEach((element: hotelType) => {
      if (element['marshaCode'].toLowerCase() === marsha.toLowerCase() && marsha.toLowerCase() !== 'test') {
        marshaMatch = true;
      }
    });
    if (marsha.toLowerCase() === 'test') {
      marshaMatch = true;
    }
  }

  if (marsha && regionList.length > 0) {
    regionList.forEach((element: Region) => {
      element.hotelsList.forEach((element: hotelType) => {
        if (element['marshaCode'].toLowerCase() === marsha.toLowerCase() && marsha.toLowerCase() !== 'test') {
          marshaMatch = true;
        }
      });
    });
    if (marsha.toLowerCase() === 'test') {
      marshaMatch = true;
    }
  }

  if (!marshaMatch) {
    marsha = '';
  }

  const defaultData: DefaultData = {
    propertiesOfInterest: [
      { key: 'hotelOne', value: { key: '', value: '' }, isError: false },
      { key: 'hotelTwo', value: { key: '', value: '' }, isError: false },
      { key: 'hotelThree', value: { key: '', value: '' }, isError: false },
    ],
    eventInformation: [
      { key: 'eventName', value: '', isError: false },
      { key: 'eventAttendees', value: '', isError: false },
      { key: 'eventPurpose', value: '', isError: false },
      { key: 'roomCountOption', value: '', isError: false },
      { key: 'eventGuestRooms', value: '', isError: false },
      { key: 'eventType', value: [], isError: false },
    ],
    eventDates: [
      { key: 'decisionDates', value: null, isError: false },
      { key: 'preferredDates', value: null, isError: false },
      { key: 'flexibleDates', value: '', isError: false },
      { key: 'alternateDateOne', value: null, isError: false },
      { key: 'alternateDateTwo', value: null, isError: false },
    ],
    contactInformation: [
      { key: 'title', value: '', isError: false },
      { key: 'eventPlannerInfo', value: { key: '', value: '' }, isError: false },
      { key: 'firstName', value: '', isError: false },
      { key: 'lastName', value: '', isError: false },
      { key: 'addressLineOne', value: '', isError: false },
      { key: 'addressLineTwo', value: '', isError: false },
      { key: 'city', value: '', isError: false },
      { key: 'state', value: '', isError: false },
      { key: 'country', value: { key: '', value: '' }, isError: false },
      { key: 'postalCode', value: '', isError: false },
      { key: 'phoneNumber', value: '', isError: false },
      { key: 'emailAddress', value: '', isError: false, isEmail: true },
      { key: 'companyAssociation', value: '', isError: false },
    ],
    eventDetails: [
      { key: 'rfpPrepared', value: '', isError: false },
      { key: 'typeMeeting', value: [], isError: false },
      { key: 'keysSuccess', value: '', isError: false },
      { key: 'eventHistory', value: '', isError: false },
      { key: 'attendeeCountLargestSession', value: { key: '', value: '' }, isError: false },
      { key: 'preferredSetupLargestSession', value: { key: '', value: '' }, isError: false },
      { key: 'numBreakoutRooms', value: '', isError: false },
      { key: 'attendeeCountLargestSessionBreakout', value: '', isError: false },
      { key: 'vips', value: '', isError: false },
      { key: 'cateringRequirements', value: '', isError: false },
      { key: 'audioVisualRequirements', value: '', isError: false },
      { key: 'specialRequirements', value: '', isError: false },
      { key: 'additionalServices', value: [], isError: false },
      { key: 'specifyOther', value: '', isError: false },
      { key: 'questionsComments', value: '', isError: false },
      { key: 'weddingRequirements', value: '', isError: false },
      { key: 'rfpFile', value: null, isError: false },
      { key: 'agenda', value: '', isError: false },
    ],
    privateDining: [{ key: 'privateDining', value: '', isError: false }],
  };
  const setMandatoryFlag = (objName: string) => {
    props.model.rfpjson?.key?.instance?.[objName]?.fieldSet?.map((item: DefaultArrayData) => {
      const objIndex = defaultData[objName as keyof DefaultData]?.findIndex(obj => obj?.key === item?.name);
      if (objIndex !== -1) {
        defaultData[objName as keyof DefaultData][objIndex].mandatory = item.mandatory;
      }
    });
  };
  setMandatoryFlag('propertiesOfInterest');
  setMandatoryFlag('eventInformation');
  setMandatoryFlag('eventDates');
  setMandatoryFlag('contactInformation');
  setMandatoryFlag('eventDetails');
  setMandatoryFlag('privateDining');

  const [organismData, setOrganismData] = useState(defaultData);
  const [onClearFlag, setOnClearFlag] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [failureScenario, setFailureScenario] = useState(false);
  const [pocEmail, setPocEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchPocEmail = async () => {
      try {
        const response = await fetch(props?.model?.pocEmailJson, {
          method: 'GET',
        });

        const responseData = await response?.json();
        setPocEmail(responseData[':items']?.root[':items']?.container[':items']?.poc_email_list?.keyValueList ?? null);
      } catch (err) {
        console.error('****Error fetching POC email:', err);
      }
    };

    fetchPocEmail();
  }, []);

  const handleDataChange = (newData: DefaultObjectData, obj: string) => {
    const updatedArray: CustomType['custom'] = { ...organismData };
    const objIndex = updatedArray[obj].findIndex((obj: DefaultArrayData) => obj?.key === newData?.key);
    if (objIndex !== -1) {
      updatedArray[obj][objIndex].value = newData.value;
      if (
        updatedArray[obj][objIndex]?.mandatory === true &&
        (updatedArray[obj][objIndex]?.value !== '' || updatedArray[obj][objIndex]?.value?.key !== '')
      ) {
        updatedArray[obj][objIndex]['isError'] = false;
      }
      if (updatedArray[obj][objIndex]?.key === 'rfpFile') {
        if (updatedArray[obj][objIndex]?.value === '') {
          updatedArray[obj][objIndex]['isError'] = true;
        } else {
          updatedArray[obj][objIndex]['isError'] = false;
        }
      }
      if (updatedArray[obj][objIndex]?.key === 'agenda') {
        if (updatedArray[obj][objIndex]?.value === '') {
          updatedArray[obj][objIndex]['isError'] = true;
        } else {
          updatedArray[obj][objIndex]['isError'] = false;
        }
      }
      setOrganismData(updatedArray);
      setOnClearFlag(false);
    }
  };
  const onSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const tempObj = { ...organismData };
    let hasError = false;
    const payload: CustomType['custom'] = {};
    payload.formType = props?.model.rfpjson?.key?.instanceType ?? '';
    const propertyData: object[] = [];
    const hotelList = props?.model.rfpjson?.key?.instance?.propertiesOfInterest?.hotelList || [];
    const regionList = props?.model.rfpjson?.key?.instance?.propertiesOfInterest?.regions || [];
    let errorAt = '';

    tempObj['propertiesOfInterest'].map((item: DefaultArrayData, index: number) => {
      if (
        typeof item?.value === 'object' &&
        !Array.isArray(item?.value) &&
        item['value'] &&
        item?.mandatory === true &&
        item?.value?.key === ''
      ) {
        if (!marsha) {
          item.isError = true;
          hasError = true;
          if (!errorAt) {
            errorAt = item.key;
          }
        } else {
          if (hotelList.length > 0) {
            const hotel = hotelList.find((i: Record<string, string>) => {
              return marsha?.toLowerCase() === i['marshaCode'].toLowerCase();
            });
            const pocEmailIdValue = findEmailByMarshaCode(hotel?.marshaCode, pocEmail);
            if (marsha.toLowerCase() !== 'test') {
              payload.hotelOne = hotel?.hotelName ?? '';
              propertyData.push({
                marshaCode: hotel?.marshaCode ?? '',
                propOwnerEmail: pocEmailIdValue ?? '',
                shortTitle: hotel?.shortTitle,
                hotelName: hotel?.hotelName,
              });
            }
            if (marsha.toLowerCase() === 'test') {
              payload.hotelOne = 'Test';
              propertyData.push({
                marshaCode: marsha.toUpperCase(),
                propOwnerEmail: router?.query?.['pocEmail']?.toString() ?? '',
                shortTitle: 'Test',
                hotelName: 'Test',
              });
            }
          } else if (regionList.length > 0) {
            let hotel: CustomType['custom'] = [];
            regionList.forEach((element: Region) => {
              element.hotelsList.forEach((ele: hotelType) => {
                if (marsha?.toLowerCase() === ele['marshaCode'].toLowerCase()) {
                  hotel = ele;
                }
              });
            });
            if (marsha?.toLowerCase() !== 'test') {
              const pocEmailIdValue = findEmailByMarshaCode(hotel?.marshaCode, pocEmail);
              payload.hotelOne = hotel?.hotelName ?? '';
              propertyData.push({
                marshaCode: hotel?.marshaCode ?? '',
                propOwnerEmail: pocEmailIdValue ?? '',
                shortTitle: hotel?.shortTitle,
                hotelName: hotel?.hotelName,
              });
            }
            if (marsha.toLowerCase() === 'test') {
              payload.hotelOne = 'Test';
              propertyData.push({
                marshaCode: marsha.toUpperCase(),
                propOwnerEmail: router?.query?.['pocEmail']?.toString() ?? '',
                shortTitle: 'Test',
                hotelName: 'Test',
              });
            }
          }
        }
      } else {
        item.isError = false;
        if (typeof item?.value === 'object' && !Array.isArray(item?.value) && item['value']) {
          if (index === 0) {
            payload.hotelOne = item['value']['value'];
          }
          if (index === 1) {
            payload.hotelTwo = item['value']['value'];
          }
          if (index === 2) {
            payload.hotelThree = item['value']['value'];
          }
          if (item['value']['value'] && item['value']) {
            if (hotelList.length > 0) {
              const hotel = hotelList.find((i: Record<string, string>) => {
                if (typeof item?.value === 'object' && !Array.isArray(item?.value) && item['value']) {
                  return i['marshaCode'] === item['value']['key'];
                }
              });
              const pocEmailIdValue = findEmailByMarshaCode(hotel.marshaCode, pocEmail);
              propertyData.push({
                marshaCode: hotel?.marshaCode ?? '',
                propOwnerEmail: pocEmailIdValue ?? '',
                shortTitle: hotel?.shortTitle,
                hotelName: hotel?.hotelName,
              });
            } else if (regionList.length > 0) {
              let hotel: CustomType['custom'] = [];
              regionList.forEach((element: Region) => {
                element.hotelsList.forEach((ele: { marshaCode: string }) => {
                  if (
                    typeof item?.value === 'object' &&
                    !Array.isArray(item?.value) &&
                    item['value'] &&
                    ele['marshaCode'] === item['value']['key']
                  ) {
                    hotel = ele;
                  }
                });
              });
              const pocEmailIdValue = findEmailByMarshaCode(hotel.marshaCode, pocEmail);
              propertyData.push({
                marshaCode: hotel?.marshaCode ?? '',
                propOwnerEmail: pocEmailIdValue ?? '',
                shortTitle: hotel?.shortTitle,
                hotelName: hotel?.hotelName,
              });
            }
          }
        }
      }
    });
    payload.rfpRequestURL = window.location.href;
    payload.dateFormatPattern = getRfpSubmissionDateFormatPattern(document?.documentElement?.lang);
    payload.propertyData = propertyData;
    tempObj['eventInformation'].map((item: DefaultArrayData) => {
      if (item?.mandatory === true && item?.value === '') {
        if (item.key === 'eventGuestRooms') {
          const roomCountOption = organismData.eventInformation.find(i => {
            return i.key === 'roomCountOption';
          });
          if (roomCountOption?.value !== (props.model?.rfpjson?.key?.instance?.labels?.noLabel ?? 'No')) {
            item.isError = true;
            hasError = true;
            if (!errorAt) {
              errorAt = item.key;
            }
          }
        } else {
          item.isError = true;
          hasError = true;
          if (!errorAt) {
            errorAt = item.key;
          }
        }
      } else {
        item.isError = false;
        if (item.value) {
          payload[item.key] = item?.value?.toString();
        }
      }
    });
    tempObj['eventDates'].map((item: DefaultArrayData) => {
      if (item?.mandatory === true && (item?.value == null || item?.value === '')) {
        if (item.key === 'alternateDateOne' || item.key === 'alternateDateTwo') {
          const roomCountOption = organismData.eventDates.find(i => {
            return i.key === 'flexibleDates';
          });
          if (roomCountOption?.value !== (props.model?.rfpjson?.key?.instance?.labels?.noLabel ?? 'No')) {
            item.isError = true;
            hasError = true;
            if (!errorAt) {
              errorAt = item.key;
            }
          }
        } else {
          item.isError = true;
          hasError = true;
          if (!errorAt) {
            errorAt = item.key;
          }
        }
      } else {
        item.isError = false;
        payload[item.key] = item.value;
      }
    });
    tempObj['contactInformation'].map((item: DefaultArrayData) => {
      if (
        item?.mandatory === true &&
        (item?.value === '' ||
          (typeof item?.value === 'object' && !Array.isArray(item?.value) && item?.value?.key === ''))
      ) {
        item.isError = true;
        hasError = true;
        if (!errorAt) {
          errorAt = item.key;
        }
      } else if (item.key === 'emailAddress' && typeof item?.value === 'string' && !emailRegex.test(item.value)) {
        item.isError = true;
        hasError = true;
        if (!errorAt) {
          errorAt = item.key;
        }
      } else {
        item.isError = false;
        if (
          (item.key === 'country' || item.key === 'eventPlannerInfo') &&
          item['value'] &&
          typeof item.value === 'object' &&
          !Array.isArray(item.value)
        ) {
          payload[item.key] = item['value']['value'];
        } else {
          payload[item.key] = item.value;
        }
      }
    });
    tempObj['privateDining'].map((item: DefaultArrayData) => {
      if (item?.mandatory === true && (item?.value == null || item?.value === '')) {
        item.isError = true;
        hasError = true;
        if (!errorAt) {
          errorAt = item.key;
        } else {
          item.isError = true;
          hasError = true;
          if (!errorAt) {
            errorAt = item.key;
          }
        }
      } else {
        item.isError = false;
        payload[item.key] = item.value;
      }
    });

    if (organismData.eventDetails[0].value === (props.model?.rfpjson?.key?.instance?.labels?.yesLabel ?? 'Yes')) {
      payload['rfpPrepared'] = 'Yes';
      payload['rfpPreparedLabel'] = organismData.eventDetails[0].value;
      if (organismData.eventDetails[16]?.mandatory && !organismData.eventDetails[16].value) {
        organismData.eventDetails[16].isError = true;
        hasError = true;
        if (!errorAt) {
          errorAt = organismData.eventDetails[16].key;
        }
      }
    } else if (organismData.eventDetails[0].value === (props.model?.rfpjson?.key?.instance?.labels?.noLabel ?? 'No')) {
      payload['rfpPrepared'] = 'No';
      payload['rfpPreparedLabel'] = organismData.eventDetails[0].value;
      tempObj['eventDetails'].map((item: DefaultArrayData) => {
        /**
         * checking the item length too in case if it is an array
         */
        if (
          item?.mandatory === true &&
          ((typeof item.value === 'object' && !Array.isArray(item.value) && item?.value?.key === '') ||
            item?.value === '' ||
            item?.value?.length === 0) &&
          item.key !== 'rfpFile'
        ) {
          if (item.key !== 'specifyOther') {
            item.isError = true;
            hasError = true;
            if (!errorAt) {
              errorAt = item.key;
            }
          } else {
            if (Array.isArray(tempObj['eventDetails'][12]?.value) && tempObj['eventDetails'][12]?.value.length > 0) {
              tempObj['eventDetails'][12].value.map((itemKey: string) => {
                if (itemKey === (props.model?.rfpjson?.key?.instance?.labels?.otherLabel ?? 'Other')) {
                  item.isError = true;
                  hasError = true;
                  if (!errorAt) {
                    errorAt = item.key;
                  }
                }
              });
            }
          }
        } else {
          if (item.key === 'agenda' && item.value) {
            payload['meetingAgendaDoc'] = 'Yes';
          } else if (
            (item.key === 'preferredSetupLargestSession' || item.key === 'attendeeCountLargestSession') &&
            item['value'] &&
            typeof item.value === 'object' &&
            !Array.isArray(item.value) &&
            item['value']['value'] === ''
          ) {
            payload[item.key] = item['value']['value'];
          } else {
            const fileName = (document.getElementsByClassName('file-name')[0] as HTMLSpanElement)?.innerText;
            if (item.key === 'agenda' && !item.value && fileName) {
              payload[item.key] = item?.value?.toString();
              item.isError = true;
              hasError = true;
              if (!errorAt) {
                errorAt = item.key;
              }
            } else {
              payload[item.key] = item?.value?.toString();
              item.isError = false;
            }
          }
        }
      });
    } else {
      organismData.eventDetails[0].isError = true;
      hasError = true;
    }

    setOrganismData(tempObj);

    if (!hasError) {
      submitRFP(payload);
    } else {
      const errorRef = document.getElementById(`rfp-${errorAt}`) ?? document.querySelector(`.rfp-${errorAt}`);

      if (errorRef) {
        const headerOffset = 120;
        const elementPosition = errorRef.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }
  };

  const submitRFP = async (payload: DefaultPayload) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/phoenix-trc/v1/rfpForm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const responseData = await response.json(); // Parse the JSON from the response
      if (responseData?.status === 'SUCCESS') {
        setSubmitted(true);
        setFailureScenario(false);
        setIsLoading(true);
        if (window.outerWidth < BREAKPOINT_TABLET && props?.model.rfpjson?.key?.instance?.successUrl) {
          window.location.assign(`${props?.model.rfpjson?.key?.instance?.successUrl}`);
          window._satellite['track']('RC_RFP_SUBMISSION');
        } else {
          setModalOpen(true);
          window._satellite['track']('RC_RFP_SUBMISSION');
        }
        /**
         * adds the marsha code to the datalayer obj.
         */
        const payLoadMarsha = payload?.propertyData?.map((marsha: CustomType['custom']) => marsha?.marshaCode);
        if (typeof window !== 'undefined') {
          updateDatalayerObjForRFP(window?.dataLayer, payLoadMarsha?.join(';'));
        }
      } else if (responseData?.status === 'FAILURE') {
        setFailureScenario(true);
        setModalOpen(true);
        setIsLoading(false);
        throw new Error(responseData?.message || 'Failed to submit form');
      }
    } catch (err: CustomType['custom']) {
      // Handle any network or fetch-related errors
      console.error('*******Error submitting RFP:', err);
      if (err?.response?.data?.status === 'FAILURE') {
        setFailureScenario(true);
        setModalOpen(true);
        setIsLoading(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setOnClearFlag(true);
    setOrganismData({ ...defaultData });
    setSubmitted(false);
    setFailureScenario(false);
    setIsLoading(false);
  };

  return (
    <serverData.Provider value={value}>
      <Styled data-component-name="o-trc-RFPComponent" data-testid="RFPComponent">
        <div className="container">
          <div className="">
            {!marsha && (
              <Properties
                onDataChange={(value: DefaultObjectData) => {
                  handleDataChange(value, 'propertiesOfInterest');
                }}
                onClear={onClearFlag}
                data={organismData.propertiesOfInterest}
                index={1}
                submitted={submitted}
                isTabletView={isTabletView}
              />
            )}
            <EventInformation
              onDataChange={(value: DefaultObjectData) => {
                handleDataChange(value, 'eventInformation');
              }}
              data={organismData.eventInformation}
              onClear={onClearFlag}
              index={marsha ? 1 : 2}
              submitted={submitted}
            />
            <CalendarComponent
              onDataChange={(value: DefaultObjectData) => {
                handleDataChange(value, 'eventDates');
              }}
              data={organismData.eventDates}
              onClear={onClearFlag}
              index={marsha ? 2 : 3}
              submitted={submitted}
            />
            <ContactInformation
              onDataChange={(value: DefaultObjectData) => {
                handleDataChange(value, 'contactInformation');
              }}
              data={organismData.contactInformation}
              onClear={onClearFlag}
              index={marsha ? 3 : 4}
              submitted={submitted}
              isTabletView={isTabletView}
            />
            <EventDetails
              onDataChange={(value: DefaultObjectData) => {
                handleDataChange(value, 'eventDetails');
              }}
              data={organismData.eventDetails}
              onClear={onClearFlag}
              index={marsha ? 4 : 5}
              submitted={submitted}
              isTabletView={isTabletView}
            />
            <PrivateDining
              onDataChange={(value: DefaultObjectData) => {
                handleDataChange(value, 'privateDining');
              }}
              index={marsha ? 5 : 6}
              data={organismData?.privateDining}
              submitted={submitted}
              onClear={onClearFlag}
            />
            <div
              className={`offset-xl-2 row col-12 mt-xl-5 pt-xl-5 mb-5 pb-5 justify-content-md-center justify-content-xl-start`}
            >
              <button
                type="button"
                onClick={onSubmit}
                className={clsx(
                  'rfpSubmit m-button-s m-button-primary',
                  submitted || isLoading ? 'disabled' : '',
                  props?.model.rfpjson?.key?.instance?.enableTracking ? 'custom_click_track' : ''
                )}
                disabled={isLoading ? true : false}
                {...(props?.model.rfpjson?.key?.instance?.enableTracking
                  ? {
                      'data-custom_click_track_value': `${props?.model.rfpjson?.key?.instance?.dataCustomClickTrackValue}`,
                    }
                  : {})}
              >
                {props.model.rfpjson?.key?.instance?.formSubmit?.submitButton}
              </button>
              <button type="button" onClick={onClear} className="rfpClear m-button-s m-button-secondary">
                {props.model.rfpjson?.key?.instance?.formSubmit?.clearButton}
              </button>
            </div>
          </div>
          <Modal
            open={modalOpen}
            onClose={() => {
              setModalOpen(!modalOpen);
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Styled>
              <div>
                <div className="rfp-modal-content color-scheme6">
                  <div className="rfp-modal-content__container m-5 py-5 px-2">
                    <h2 className="t-title-alt-s t-rc-italic mb-2">
                      {failureScenario
                        ? props.model.rfpjson?.key?.instance?.errorMessage?.errorEyebrowText
                        : props.model.rfpjson?.key?.instance?.successMessage?.thankYouLabel}
                    </h2>
                    <p className="t-title-m t-rc-text-transform-uppercase mb-5 rfp-modal-content__title">
                      {failureScenario
                        ? props.model.rfpjson?.key?.instance?.errorMessage?.errorTitle
                        : props.model.rfpjson?.key?.instance?.successMessage?.willBeInTouchLabel}
                    </p>
                    <p className="t-brand-font-alt-l m-0">
                      {failureScenario
                        ? props.model.rfpjson?.key?.instance?.errorMessage?.errorDetailedMessage
                        : props.model.rfpjson?.key?.instance?.successMessage?.message}
                    </p>
                    <button
                      className="rfp-modal-content--close d-flex"
                      onClick={() => {
                        setModalOpen(!modalOpen);
                      }}
                    >
                      <span className="t-font-m">{props.model.rfpjson?.key?.instance?.successMessage?.closeLabel}</span>
                      <span className="icon-clear ml-2"></span>
                    </button>
                  </div>
                </div>
              </div>
            </Styled>
          </Modal>
        </div>
      </Styled>
    </serverData.Provider>
  );
};
